import {
    GroupFileAssignmentRequest,
    GroupFilesAssignmentMode,
    GroupFilesAssignmentRequest,
    GroupPictureRole,
} from '@kcftech/prometheus-client';
import { useMutation } from '@tanstack/react-query';
import { RemoveIcon } from 'components/icon/RemoveIcon';
import { ActionMenuItem } from 'components/menu/ActionMenuItem';
import { ConfirmModal } from 'components/modal/ConfirmModal';
import { sdClient } from 'contexts/SDKClientContext';
import { tokens } from 'styles/tokens';

import type { FileActionsBase } from './base';

function useUnlinkGroupMutation(base: FileActionsBase) {
    return useMutation(
        base.actionMutationOptions({
            mutationFn: async () => {
                if (!(base.rowData.EntryType !== 'Folder' && base.group)) {
                    return;
                }
                await sdClient.assignFilesToGroups({
                    body: new GroupFilesAssignmentRequest({
                        Mode: GroupFilesAssignmentMode.Remove,
                        Assignments: [
                            new GroupFileAssignmentRequest({
                                GroupId: base.group.Id,
                                PictureId: base.rowData.Id,
                                Role: GroupPictureRole.Other,
                            }),
                        ],
                    }),
                });
            },
            errorMessage: 'Unable to remove file association.',
            onSuccess: () => {
                base.onChange();
                base.closeAction();
            },
            onError: () => base.closeAction(),
        }),
    );
}

function UnlinkGroupMenuItem({ base }: { base: FileActionsBase }) {
    if (!base.canDeleteFromFileLibrary || !base.enableModal) {
        return null;
    }
    return (
        <ActionMenuItem
            title="Remove Association"
            hoverIcon={<RemoveIcon />}
            icon={<RemoveIcon color={tokens.text.secondary} />}
            onSelect={() => base.openAction('unlink-group')}
        />
    );
}

function UnlinkGroupModal({
    base,
    onConfirm,
}: {
    base: FileActionsBase;
    onConfirm: () => void;
}) {
    if (!base.group) {
        return null;
    }
    return (
        <ConfirmModal
            warningText="The file will still be available in the File Library."
            title={base.rowData.Name}
            actionText={`remove ${base.group.Name}'s association to this file?`}
            checkText="remove this file association"
            actionButtonText="Remove"
            onAction={onConfirm}
            onClose={base.closeAction}
        />
    );
}

export const UnlinkGroup = {
    useMutation: useUnlinkGroupMutation,
    MenuItem: UnlinkGroupMenuItem,
    Modal: UnlinkGroupModal,
};
